/**
 * App.jsx — Root component (slim orchestrator)
 *
 * Responsibilities:
 *  - Authentication state (session, token, locked)
 *  - Idle-timeout lock (15 min)
 *  - BroadcastChannel cross-tab logout
 *  - Route to the correct portal: Admin / Teacher / Parent
 *
 * All pages and sub-components live in:
 *  src/admin/      — admin portal pages
 *  src/teacher/    — teacher portal pages
 *  src/auth/       — login, session picker, recovery flows
 *  src/components/ — shared UI pieces
 *  src/utils/      — pure utility helpers
 */

import React, { useState, useEffect, useCallback } from 'react';
import './index.css';

// ── Shared utilities ──────────────────────────────────────────────
import { loadDB } from './db';
import {
  clearToken, getToken,
  loadSessionData, saveSessionData, initShadow,
} from './storage';

// ── Shared components ─────────────────────────────────────────────
import AppLoader   from './components/AppLoader';
import LockScreen  from './components/LockScreen';

// ── Auth flow ─────────────────────────────────────────────────────
import LoginFlow from './auth/LoginFlow';

// ── Portal shells ─────────────────────────────────────────────────
import AdminApp      from './admin/AdminApp';
import TeacherPortal from './teacher/TeacherPortal';
import ParentPortal  from './ParentPortal';

// ─────────────────────────────────────────────────────────────────
export default function App() {
  const [db,              setDB]             = useState(null);
  const [session,         setSession]        = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [page,            setPage]           = useState('dash');
  const [loading,         setLoading]        = useState(true);

  // Persisted via sessionStorage so a hard-refresh can't bypass the lock
  const [locked,      setLocked]      = useState(() => sessionStorage.getItem('lkps_locked') === '1');
  const [lockWarning, setLockWarning] = useState(false);

  // ── Idle-timeout constants ──────────────────────────────────────
  const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 min → lock
  const WARN_BEFORE  =  2 * 60 * 1000; // warn 2 min before

  const idleTimer = React.useRef(null);
  const warnTimer = React.useRef(null);
  const clearTimers = () => { clearTimeout(idleTimer.current); clearTimeout(warnTimer.current); };

  // ── Session lock / unlock helpers ────────────────────────────────
  const lockSession = useCallback(() => {
    sessionStorage.setItem('lkps_locked', '1');
    sessionStorage.setItem('lkps_locked_at', Date.now().toString());
    setLocked(true);
    setLockWarning(false);
  }, []);

  const unlockSession = useCallback(() => {
    sessionStorage.removeItem('lkps_locked');
    sessionStorage.removeItem('lkps_locked_at');
    setLocked(false);
  }, []);

  // ── Idle timer reset (attached to user activity events) ──────────
  const resetIdleTimer = useCallback(() => {
    if (!session || locked) return;
    clearTimers();
    setLockWarning(false);
    warnTimer.current = setTimeout(() => setLockWarning(true), IDLE_TIMEOUT - WARN_BEFORE);
    idleTimer.current = setTimeout(() => lockSession(), IDLE_TIMEOUT);
  }, [session, locked, lockSession]); // eslint-disable-line

  useEffect(() => {
    if (!session) return;
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => { events.forEach(e => window.removeEventListener(e, resetIdleTimer)); clearTimers(); };
  }, [session, resetIdleTimer]);

  // ── handleLogout (defined before visibility useEffect) ──────────
  const handleLogout = useCallback(() => {
    clearToken();
    localStorage.removeItem('lkps_user');
    localStorage.removeItem('lkps_user_pic');
    localStorage.removeItem('lkps_active_session');
    sessionStorage.removeItem('lkps_locked');
    sessionStorage.removeItem('lkps_locked_at');
    setSession(null);
    setActiveSessionId(null);
    setDB(null);
    unlockSession();
    setLockWarning(false);
    clearTimers();
    try { const bc = new BroadcastChannel('lkps_auth'); bc.postMessage('logout'); bc.close(); } catch (e) { }
  }, [unlockSession]);

  // ── BroadcastChannel — sync logout across browser tabs ──────────
  useEffect(() => {
    const bc = new BroadcastChannel('lkps_auth');
    bc.onmessage = (e) => {
      if (e.data === 'logout') {
        clearToken();
        sessionStorage.removeItem('lkps_locked');
        sessionStorage.removeItem('lkps_locked_at');
        setSession(null); setActiveSessionId(null); setDB(null);
      }
    };
    return () => bc.close();
  }, []);

  // ── Visibility change — re-lock, re-sync ─────────────────────────
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const wasLocked = sessionStorage.getItem('lkps_locked') === '1';
      if (wasLocked) setLocked(true);
      if (!getToken() && session) { handleLogout(); return; }
      if (!wasLocked && getToken() && activeSessionId) {
        loadSessionData(activeSessionId).then(raw => {
          if (raw) {
            const merged = { ...loadDB(), ...raw };
            initShadow(activeSessionId, merged);
            setDB(merged);
          }
        }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [session, activeSessionId, handleLogout]);

  // ── On mount: restore session from localStorage ──────────────────
  useEffect(() => {
    const token    = getToken();
    const savedUser   = (() => { try { return JSON.parse(localStorage.getItem('lkps_user')); } catch { return null; } })();
    const savedSessId = localStorage.getItem('lkps_active_session');
    const wasLocked   = sessionStorage.getItem('lkps_locked') === '1';

    if (token && savedUser && savedSessId) {
      if (savedUser.pic === '__stored__') savedUser.pic = localStorage.getItem('lkps_user_pic') || '';
      const role = savedUser._portalRole === 'teacher' ? 'teacher'
                 : savedUser._portalRole === 'parent'  ? 'parent'
                 : savedUser.role        === 'teacher'  ? 'teacher'
                 : savedUser.role        === 'parent'   ? 'parent'
                 : 'admin';
      setSession({ user: savedUser, role });
      setActiveSessionId(savedSessId);
      if (wasLocked) setLocked(true);
    } else {
      clearToken();
      localStorage.removeItem('lkps_user');
      localStorage.removeItem('lkps_user_pic');
      localStorage.removeItem('lkps_active_session');
      sessionStorage.removeItem('lkps_locked');
      sessionStorage.removeItem('lkps_locked_at');
    }
    setLoading(false);
  }, []);

  // ── Load session data from API when activeSessionId changes ──────
  useEffect(() => {
    if (!activeSessionId) return;
    setDB(null);
    loadSessionData(activeSessionId).then(raw => {
      const defaults = loadDB();
      const merged   = raw ? { ...defaults, ...raw } : defaults;
      initShadow(activeSessionId, merged);
      setDB(merged);
      // Sync admin photo from backend record
      setSession(prev => {
        if (!prev || prev.role !== 'admin') return prev;
        const adminRecord = (merged.admins || []).find(a =>
          a.username === prev.user.username || a.id === prev.user.id || a._id === prev.user._id
        );
        if (adminRecord?.photo && adminRecord.photo !== prev.user.pic) {
          const updatedUser = { ...prev.user, pic: adminRecord.photo };
          try { localStorage.setItem('lkps_user_pic', adminRecord.photo); } catch (e) { }
          return { ...prev, user: updatedUser };
        }
        return prev;
      });
    }).catch(() => setDB(loadDB()));
  }, [activeSessionId]);

  // ── save() — persists db to state + API ─────────────────────────
  const save = useCallback((newDB) => {
    setDB(newDB);
    if (activeSessionId) saveSessionData(activeSessionId, newDB);
  }, [activeSessionId]);

  // ── handleLogin ──────────────────────────────────────────────────
  const handleLogin = (authSession, sessId) => {
    setSession(authSession);
    setActiveSessionId(sessId);
    unlockSession();
    setLockWarning(false);
    const u = authSession.user;
    const userToStore = { ...u, _portalRole: authSession.role, pic: u.pic ? '__stored__' : '' };
    try {
      if (u.pic) localStorage.setItem('lkps_user_pic', u.pic);
      localStorage.setItem('lkps_user', JSON.stringify(userToStore));
    } catch (e) {
      localStorage.setItem('lkps_user', JSON.stringify({ ...userToStore, pic: '' }));
    }
    localStorage.setItem('lkps_active_session', sessId);
    try { const bc = new BroadcastChannel('lkps_auth'); bc.postMessage('login'); bc.close(); } catch (e) { }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (loading)                       return <AppLoader />;
  if (!session || !activeSessionId)  return <LoginFlow onLogin={handleLogin} skipAuth={session} authSession={session} />;
  if (!db)                           return <AppLoader label="Loading session…" />;

  if (locked && session) {
    return (
      <LockScreen
        user={session.user}
        role={session.role}
        activeSessionId={activeSessionId}
        onUnlock={(ok) => { if (ok) { unlockSession(); resetIdleTimer(); } }}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <>
      {/* Idle-timeout warning banner */}
      {lockWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#b45309', color: '#fff',
          padding: '10px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', fontSize: 13, fontWeight: 600,
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
        }}>
          <span>⚠️ You'll be locked out in 2 minutes due to inactivity.</span>
          <button onClick={resetIdleTimer} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            padding: '5px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12,
          }}>Stay Active</button>
        </div>
      )}

      {session.role === 'teacher' ? (
        <TeacherPortal db={db} save={save} teacher={session.user} activeSessionId={activeSessionId} onLogout={handleLogout} />
      ) : session.role === 'parent' ? (
        <ParentPortal db={db} student={session.user} activeSessionId={activeSessionId} onLogout={handleLogout} />
      ) : (
        <AdminApp
          db={db} save={save}
          page={page} setPage={setPage}
          user={session.user}
          setUser={u => {
            setSession(s => ({ ...s, user: u }));
            try {
              if (u.pic) localStorage.setItem('lkps_user_pic', u.pic);
              localStorage.setItem('lkps_user', JSON.stringify({ ...u, pic: u.pic ? '__stored__' : '' }));
            } catch (e) {
              localStorage.setItem('lkps_user', JSON.stringify({ ...u, pic: '' }));
            }
          }}
          activeSessionId={activeSessionId}
          onSwitchSession={() => { setActiveSessionId(null); localStorage.removeItem('lkps_active_session'); }}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}
